import { apiFetch } from './client'
import type {
  InvoiceCreate,
  InvoiceDetail,
  InvoiceListItem,
  InvoiceUpdate,
  LineItem,
  LineItemCreate,
  SuggestedLineItem,
} from '../types/invoices'

export interface InvoiceListParams {
  practice_id?: number
  client_id?: number
  patient_id?: number
  status?: string
  date_from?: string
  date_to?: string
  limit?: number
  offset?: number
}

export async function listInvoices(params: InvoiceListParams = {}): Promise<InvoiceListItem[]> {
  const qs = new URLSearchParams()
  Object.entries(params).forEach(([k, v]) => {
    if (v !== undefined && v !== null) qs.set(k, String(v))
  })
  const res = await apiFetch(`/api/invoices?${qs.toString()}`)
  if (!res.ok) throw new Error('Failed to load invoices')
  return res.json() as Promise<InvoiceListItem[]>
}

export async function createInvoice(data: InvoiceCreate): Promise<InvoiceDetail> {
  const res = await apiFetch('/api/invoices', {
    method: 'POST',
    body: JSON.stringify(data),
  })
  if (!res.ok) {
    const err = (await res.json()) as { detail?: string }
    throw new Error(typeof err.detail === 'string' ? err.detail : 'Failed to create invoice')
  }
  return res.json() as Promise<InvoiceDetail>
}

export async function getInvoice(id: number): Promise<InvoiceDetail> {
  const res = await apiFetch(`/api/invoices/${id}`)
  if (res.status === 404) throw new Error('Invoice not found')
  if (!res.ok) throw new Error('Failed to load invoice')
  return res.json() as Promise<InvoiceDetail>
}

export async function updateInvoice(id: number, data: InvoiceUpdate): Promise<InvoiceDetail> {
  const res = await apiFetch(`/api/invoices/${id}`, {
    method: 'PATCH',
    body: JSON.stringify(data),
  })
  if (!res.ok) {
    const err = (await res.json()) as { detail?: string }
    throw new Error(typeof err.detail === 'string' ? err.detail : 'Failed to update invoice')
  }
  return res.json() as Promise<InvoiceDetail>
}

export async function cancelInvoice(id: number): Promise<void> {
  const res = await apiFetch(`/api/invoices/${id}`, { method: 'DELETE' })
  if (!res.ok) throw new Error('Failed to cancel invoice')
}

export async function addLineItem(invoiceId: number, data: LineItemCreate): Promise<LineItem> {
  const res = await apiFetch(`/api/invoices/${invoiceId}/line-items`, {
    method: 'POST',
    body: JSON.stringify(data),
  })
  if (!res.ok) {
    const err = (await res.json()) as { detail?: string }
    throw new Error(typeof err.detail === 'string' ? err.detail : 'Failed to add line item')
  }
  return res.json() as Promise<LineItem>
}

export async function removeLineItem(invoiceId: number, itemId: number): Promise<void> {
  const res = await apiFetch(`/api/invoices/${invoiceId}/line-items/${itemId}`, {
    method: 'DELETE',
  })
  if (!res.ok) throw new Error('Failed to remove line item')
}

export async function getSuggestedLineItems(consultationId: number): Promise<SuggestedLineItem[]> {
  const res = await apiFetch(`/api/invoices/suggested-line-items?consultation_id=${consultationId}`)
  if (!res.ok) throw new Error('Failed to load suggested line items')
  return res.json() as Promise<SuggestedLineItem[]>
}
